"use client";

import { logger } from "@/lib/logger";

import { ClocksWidget } from "./renderers/clocks-widget";
import { GaugeWidget } from "./renderers/gauge-widget";
import { ListWidget } from "./renderers/list-widget";
import { SeriesWidget } from "./renderers/series-widget";
import { StatWidget } from "./renderers/stat-widget";

import type { WidgetPayload } from "@/types";

/**
 * The one and only place a widget's `type` maps to a component.
 *
 * Deliberately NOT exhaustiveness-checked with a `never` assertion: the union is
 * a compile-time contract but the wire is not, so a backend that has shipped a
 * renderer type newer than this bundle must degrade to an empty card rather than
 * throw and take the dashboard down with it.
 */
// Payload types this bundle can actually draw.
//
// The backend deploys independently, so it can start sending a renderer type
// older clients don't have. Rendering an empty card with a title in that case
// looks broken; the dashboard uses this to skip the widget entirely until the
// frontend catches up.
const RENDERABLE: ReadonlySet<string> = new Set([
  "list",
  "series",
  "stat",
  "gauge",
  "clocks",
]);

export function canRenderPayload(payload: WidgetPayload): boolean {
  return RENDERABLE.has(payload.type);
}

export function WidgetRenderer({ payload }: { payload: WidgetPayload }) {
  switch (payload.type) {
    case "list":
      return <ListWidget payload={payload} />;
    case "series":
      return <SeriesWidget payload={payload} />;
    case "stat":
      return <StatWidget payload={payload} />;
    case "gauge":
      return <GaugeWidget payload={payload} />;
    case "clocks":
      return <ClocksWidget payload={payload} />;
    default:
      logger.warn("[dashboard] unknown widget payload type", payload);
      return null;
  }
}
